import React, { useState, useCallback, useEffect, useRef } from 'react';
import { chaseService, ChaseResult, ChaseDecision } from '../../services/chaseService';
import { User } from '../../types';

interface ChaseProps {
  user: User;
  onUserUpdate: (updates: Partial<User>) => void;
  onUpdate: () => void;
}

const Chase: React.FC<ChaseProps> = ({ user, onUserUpdate, onUpdate }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentChase, setCurrentChase] = useState<ChaseDecision | null>(null);
  const [lastResult, setLastResult] = useState<ChaseResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(15);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleStartChase = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setLastResult(null);

    try {
      const result = await chaseService.startChase();
      
      if (result.success) {
        setCurrentChase(result.nextDecision || null);
        setTimeLeft(15);
        setIsTimerActive(true);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleTimerTimeout = useCallback(async () => {
    try {
      const result = await chaseService.handleTimerTimeout();
      
      // Update user data if there were changes
      if (result.rankPointsEarned || result.moneyEarned || result.moneyLost || result.healthLost) {
        const updates: Partial<User> = {};
        if (result.rankPointsEarned) updates.points = user.points + result.rankPointsEarned;
        if (result.moneyEarned) updates.money = user.money + result.moneyEarned;
        if (result.moneyLost) updates.money = Math.max(0, user.money - result.moneyLost);
        if (result.healthLost) updates.health = Math.max(0, user.health - result.healthLost);
        if (result.outcome === 'jailed') {
          updates.status = 'Jailed';
          updates.jailTime = result.jailTime || 0;
          updates.jailedAt = new Date().toISOString();
        }
        onUserUpdate(updates);
      }
      
      setLastResult(result);
      onUpdate(); // Refresh user data from server
    } catch (err: any) {
      setError(err.message);
    }
  }, [user, onUserUpdate, onUpdate]);

  // Timer effect
  useEffect(() => {
    if (isTimerActive && timeLeft > 0) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (isTimerActive && timeLeft === 0) {
      // Timer ran out - player is caught
      setIsTimerActive(false);
      setCurrentChase(null);
      handleTimerTimeout();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isTimerActive, timeLeft, handleTimerTimeout]);

  const handleMakeDecision = useCallback(async (decisionIndex: number) => {
    if (!currentChase) return;

    setIsLoading(true);
    setError(null);
    setIsTimerActive(false); // Stop timer when making decision

    try {
      const result = await chaseService.makeDecision(decisionIndex);

      // Update user data if there were changes
      if (result.rankPointsEarned || result.moneyEarned || result.moneyLost || result.healthLost) {
        const updates: Partial<User> = {};
        if (result.rankPointsEarned) updates.points = user.points + result.rankPointsEarned;
        if (result.moneyEarned) updates.money = user.money + result.moneyEarned;
        if (result.moneyLost) updates.money = Math.max(0, user.money - result.moneyLost);
        if (result.healthLost) updates.health = Math.max(0, user.health - result.healthLost);
        if (result.jailTime) updates.status = 'Jailed';
        
        onUserUpdate(updates);
      }

      if (result.outcome === 'continue' && result.nextDecision) {
        // For continue outcomes, just move to next decision without showing result
        setCurrentChase(result.nextDecision);
        setTimeLeft(15);
        setIsTimerActive(true); // Restart timer for next decision
        setLastResult(null); // Clear any previous result
      } else {
        // For success, jailed, or failed outcomes, show the result
        setLastResult(result);
        setCurrentChase(null);
        // Refresh user data from server to show updated stats
        onUpdate();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentChase, user, onUserUpdate, onUpdate]);

  const handleResetTimer = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await chaseService.resetChaseTimer();
      if (result.success) {
        setError(null);
        // Show success message temporarily
        setError(`✅ ${result.message}`);
        setTimeout(() => setError(null), 3000);
        // Refresh user data from server
        onUpdate();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [onUpdate]);

  const canStartChase = user.rankId >= 5 && user.status !== 'Jailed';
  
  // Check if user is on cooldown
  const isOnCooldown = user.lastChaseTime && 
    (new Date().getTime() - new Date(user.lastChaseTime).getTime()) < 15 * 60 * 1000; // 15 minutes
  
  const getCooldownTimeLeft = () => {
    if (!user.lastChaseTime) return 0;
    const timePassed = new Date().getTime() - new Date(user.lastChaseTime).getTime();
    const cooldownMs = 15 * 60 * 1000; // 15 minutes
    return Math.max(0, cooldownMs - timePassed);
  };
  
  const formatCooldownTime = (ms: number) => {
    const minutes = Math.floor(ms / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="chase-container">
      <div className="chase-header">
        <h2>🚔 Police Chase</h2>
        <p>Escape from the police in a high-speed pursuit through NYC streets!</p>
      </div>

      {!canStartChase && (
        <div className="chase-requirements">
          {user.rankId < 5 && (
            <div className="requirement-not-met">
              ⚠️ You need to be at least rank 5 to participate in police chases!
            </div>
          )}
          {user.status === 'Jailed' && (
            <div className="requirement-not-met">
              ⚠️ You cannot participate in chases while in jail!
            </div>
          )}
        </div>
      )}

      {canStartChase && !currentChase && !lastResult && (
        <div className="chase-start">
          <div className="chase-actions">
            <button 
              className="btn btn-primary btn-start-chase"
              onClick={handleStartChase}
              disabled={isLoading}
            >
              {isLoading ? 'Starting...' : '🚔 Start Chase'}
            </button>
            
            <button 
              className="btn btn-secondary btn-reset-timer"
              onClick={handleResetTimer}
              disabled={isLoading}
              title="Reset cooldown timer for testing"
            >
              {isLoading ? 'Resetting...' : '⏰ Reset Timer'}
            </button>
          </div>
        </div>
      )}

      {currentChase && (
        <div className="chase-decision">
          <div className="decision-header">
            <div className="decision-location">
              <h3>📍 {currentChase.location}</h3>
              <p>The police are closing in! What's your move?</p>
            </div>
            
            {isTimerActive && (
              <div className="chase-timer">
                <div className={`timer-circle ${timeLeft <= 5 ? 'danger' : timeLeft <= 10 ? 'warning' : 'normal'}`}>
                  <span className="timer-text">{timeLeft}</span>
                </div>
                <p className="timer-label">seconds left</p>
              </div>
            )}
          </div>
          
          <div className="decision-options">
            {currentChase.options.map((option, index) => (
              <div
                key={index}
                className="decision-card"
                onClick={() => !isLoading && handleMakeDecision(index)}
              >
                <div className="card-content">
                  <div className="card-icon">🚗</div>
                  <div className="card-text">{option}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {lastResult && (
        <div className="chase-result">
          <div className={`result-message ${lastResult.outcome}`}>
            <h3>
              {lastResult.outcome === 'success' && '🎉 Success!'}
              {lastResult.outcome === 'jailed' && '🚔 Caught!'}
              {lastResult.outcome === 'failed' && '💥 Failed!'}
              {lastResult.outcome === 'continue' && '🏃 Still Running!'}
            </h3>
            <p>{lastResult.message}</p>
            
            {lastResult.rankPointsEarned && (
              <p className="reward">+{lastResult.rankPointsEarned} rank points</p>
            )}
            {lastResult.moneyEarned && (
              <p className="reward">+${lastResult.moneyEarned.toLocaleString()}</p>
            )}
            {lastResult.moneyLost && (
              <p className="penalty">-${lastResult.moneyLost.toLocaleString()}</p>
            )}
            {lastResult.healthLost && (
              <p className="penalty">-{lastResult.healthLost} health</p>
            )}
            {lastResult.jailTime && (
              <p className="penalty">Jail time: {Math.floor(lastResult.jailTime / 60)} minutes</p>
            )}
          </div>

          {lastResult.outcome !== 'continue' && (
            <div className="chase-actions">
              {isOnCooldown ? (
                <div className="cooldown-info">
                  <div className="cooldown-message">
                    ⏰ Chase on cooldown: {formatCooldownTime(getCooldownTimeLeft())} remaining
                  </div>
                  <button 
                    className="btn btn-primary"
                    disabled={true}
                  >
                    🚔 Start New Chase (Cooldown)
                  </button>
                </div>
              ) : (
                <button 
                  className="btn btn-primary"
                  onClick={handleStartChase}
                  disabled={isLoading}
                >
                  {isLoading ? 'Starting...' : '🚔 Start New Chase'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
        </div>
      )}
    </div>
  );
};

export default Chase;
